import React from 'react'

import { normalizePositionTitle, PositionTitles } from '../models/PositionTitles.model'
import { UserDTO } from '../models/UserDTO.model'

interface UserAvatarProps {
  className?: string
  size?: 'sm' | 'md' | 'lg' | 'xl'
  userDetails: UserDTO
}

const initialsFromText = (text: string): string =>
  text
    .replace(/\([^)]+\)\s*$/, '')
    .trim()
    .split(/[\s/]+/)
    .filter((word) => word.length > 0)
    .map((word) => word[0])
    .join('')
    .toUpperCase()

/** Short badge for avatar circles — codes like RT/RN when possible, otherwise label initials. */
const getAvatarBadgeFromEntry = ([label, value]: [string, string]): string => {
  const paren = label.match(/\(([^)]+)\)\s*$/)
  if (paren) {
    const first = paren[1].trim().split('/')[0].trim()
    if (first) return first.toUpperCase()
  }

  const compact = value.split('/')[0].trim()
  if (compact.length <= 5 && !compact.includes(' ')) {
    return compact.toUpperCase()
  }

  return initialsFromText(label)
}

/**
 * Discipline label for the avatar — from API/Auth0 `position_title` only (never the user's name).
 * Uses PositionTitles first so stored codes like "RT" always map correctly; heuristics for odd strings.
 */
const getDisciplineLabel = (positionTitle: string): string | null => {
  if (!positionTitle?.trim()) return null
  const trimmed = normalizePositionTitle(positionTitle)

  const entries = Object.entries(PositionTitles) as [string, string][]
  const byStoredValue = entries.find(([, v]) => v === trimmed)
  if (byStoredValue) return getAvatarBadgeFromEntry(byStoredValue)

  const byLabelKey = entries.find(([k]) => k === trimmed)
  if (byLabelKey) return getAvatarBadgeFromEntry(byLabelKey)

  // Trailing "(ABBR)" or "(A/B)" from full job titles
  const paren = trimmed.match(/\(([^)]+)\)\s*$/)
  if (paren) {
    const inner = paren[1].trim()
    const first = inner.split('/')[0].trim()
    if (first) return first.toUpperCase()
  }

  const primary = trimmed.split('/')[0].trim()
  if (primary.length <= 5 && !primary.includes(' ')) return primary.toUpperCase()

  return initialsFromText(primary) || null
}

const sizeClasses: Record<NonNullable<UserAvatarProps['size']>, string> = {
  lg: 'h-14 w-14 text-base',
  md: 'h-10 w-10 text-sm',
  sm: 'h-8 w-8 text-xs',
  xl: 'h-24 w-24 text-2xl',
}

const iconSizes: Record<NonNullable<UserAvatarProps['size']>, number> = {
  lg: 28,
  md: 22,
  sm: 18,
  xl: 52,
}

/** Generic person silhouette shown when no healthcare role is set */
const PersonIcon = ({ size }: { size: number }) => (
  <svg fill="white" height={size} viewBox="0 0 24 24" width={size} xmlns="http://www.w3.org/2000/svg">
    <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
  </svg>
)

/**
 * Auth0 letter avatars (often the first letter(s) of the user's name — not discipline).
 * Match direct CDN, proxied URLs, and Gravatar fallbacks.
 */
const isAuth0LetterAvatar = (url: string): boolean => {
  const u = url.toLowerCase()
  return (
    u.includes('cdn.auth0.com/avatars') ||
    u.includes('auth0.com%2favatars') ||
    (u.includes('gravatar.com') && (u.includes('auth0') || u.includes('cdn.auth0')))
  )
}

export const UserAvatar: React.FC<UserAvatarProps> = ({ className = '', size = 'md', userDetails }) => {
  const containerClass = sizeClasses[size]
  const disciplineLabel = getDisciplineLabel(userDetails.position_title)

  const picture = userDetails.picture?.trim() ?? ''
  const isPlaceholderPicture = !picture || isAuth0LetterAvatar(picture)

  // When we know the user's discipline, always show those initials — not Auth0's "S" from their name/email.
  if (!disciplineLabel && picture && !isPlaceholderPicture) {
    return (
      <img
        alt="profile avatar"
        className={`${containerClass} rounded-full object-cover ring-2 ring-white/90 shadow-sm ${className}`}
        referrerPolicy="no-referrer"
        src={picture}
      />
    )
  }

  // Teal discipline badge (top nav + profile): matches browse header / brand green
  return (
    <div
      className={`${containerClass} flex select-none items-center justify-center rounded-full bg-aqua-mint font-bold text-white shadow-sm ring-2 ring-white/90 ${className}`}
    >
      {disciplineLabel ? disciplineLabel : <PersonIcon size={iconSizes[size]} />}
    </div>
  )
}
